import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so every component using this hook shares the same fetch.
let _redirectsPromise = null;
function fetchRedirects() {
  if (!_redirectsPromise) {
    _redirectsPromise = axios
      .get(`${API}/seo-public/redirects`)
      .then((r) => Array.isArray(r.data) ? r.data : [])
      .catch(() => []);
  }
  return _redirectsPromise;
}

function normalisePath(p) {
  if (!p) return "/";
  let out = String(p).split("?")[0].split("#")[0];
  if (out.length > 1 && out.endsWith("/")) out = out.slice(0, -1);
  return out.toLowerCase();
}

function extractPath(toPath) {
  // to_path may be an absolute URL or a /path. Normalise to pathname-only.
  try {
    if (toPath.startsWith("http")) return normalisePath(new URL(toPath).pathname);
  } catch (_e) { /* fall through */ }
  return normalisePath(toPath);
}

/**
 * useSlugAlias — When a product/solution/page renames its slug for SEO, the SEO
 * agency creates a 301 in the Redirects tab (old → new). The redirect handler
 * sends visitors of OLD to NEW correctly, but the NEW URL itself doesn't exist
 * in the hardcoded product/solution database and would render "Not Found".
 *
 * This hook resolves the situation: for the current URL `pathPrefix/<slug>`,
 * it looks through the redirect table for any rule whose `to_path` is the
 * current URL. If found, it returns the source slug (extracted from `from_path`)
 * so the calling page can render the original content under the new URL.
 *
 * Returns: { aliasSlug: string|null, loading: bool }
 */
export default function useSlugAlias(pathPrefix, currentSlug) {
  const [aliasSlug, setAliasSlug] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!currentSlug) {
      setAliasSlug(null);
      setLoading(false);
      return;
    }
    const prefix = (pathPrefix || "").replace(/\/$/, "");
    const startingHere = normalisePath(`${prefix}/${currentSlug}`);
    fetchRedirects().then((rules) => {
      if (cancelled) return;
      const active = (rules || []).filter(
        (rule) => rule && rule.is_active !== false && rule.from_path && rule.to_path,
      );
      // Walk the chain BACKWARDS: current URL is a `to_path` — find the rule
      // pointing to it, hop to that rule's `from_path`, repeat. Stop when the
      // source is a real page under the same prefix (no more incoming rules),
      // or a loop/limit is hit. This handles Speicher's chained-fix scenario
      // (old-slug → typo-slug → correct-slug) so the final URL still resolves
      // back to the original React source component.
      let here = startingHere;
      const visited = new Set([here]);
      let source = null;
      for (let hop = 0; hop < 5; hop += 1) {
        const inbound = active.find((r) => extractPath(r.to_path) === here);
        if (!inbound) break;
        const from = extractPath(inbound.from_path);
        if (visited.has(from)) break; // loop protection
        visited.add(from);
        // from_path must be on the same prefix and have a slug remainder
        if (!from.startsWith(`${prefix}/`)) break;
        source = from;
        here = from; // keep walking backwards
      }
      if (source) {
        const sourceSlug = source.substring(prefix.length + 1);
        setAliasSlug(sourceSlug || null);
      } else {
        setAliasSlug(null);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [pathPrefix, currentSlug]);

  return { aliasSlug, loading };
}
